<template>
  <Sidesheet
    :active="active"
    :hide-footer="true"
    @close="close"
  >
    <template #title>
      ТМЦ
      <span
        v-if="hasItem"
        class="guid"
      >
        №{{ item.guid }}
      </span>
    </template>
    <div class="wrapper">
      <Loader
        v-if="busy"
        label="Данные загружаются"
        height="300"
        :active="busy"
      />
      <div
        v-else
        class="content"
      >
        <div
          v-if="hasItem"
          class="name"
        >
          {{ item.name }}
        </div>
        <div class="path">
          {{ path }}
        </div>
        <div
          v-if="hasItem"
          class="parameters"
        >
          <div class="parameters__item">
            Срок годности: <b>{{ item.exp_date || '—' }}</b>
          </div>
          <div class="parameters__item">
            Примерная стоимость: <b>{{ formatAsCurrency(item.cost) || '—' }}</b>
          </div>
          <div class="parameters__item">
            Дата производства: <b>{{ item.manuf_date || '—' }}</b>
          </div>
          <div class="parameters__item">
            Дата покупки: <b>{{ item.purch_date || '—' }}</b>
          </div>
          <div class="parameters__item">
            Модель: <b>{{ item.model || '—' }}</b>
          </div>
          <div class="parameters__item">
            Где приобретено: <b>{{ item.purch_place || '—' }}</b>
          </div>
        </div>
        <Switcher
          v-if="sections.length > 0"
          v-model="currentSection"
          class="h-switcher"
          type="group"
          :items="tabs"
        />
        <template v-if="!!currentSectionData">
          <div v-if="currentSectionDocuments.length > 0">
            <files
              :files="currentSectionDocuments"
              actions="loadFiles,preview"
            />
          </div>
          <div
            v-if="currentSectionData.description"
            class="description"
          >
            {{ currentSectionData.description }}
          </div>
          <div
            v-if="currentSectionData.link"
            class="link"
          >
            <a
              :href="currentSectionData.link"
              target="_blank"
            >
              {{ currentSectionData.link }}
            </a>
            <button
              class="link__copy"
              @click="copyLinkToClipboard"
            >
              <svg-icon
                name="copy"
                :size="16"
              />
            </button>
          </div>
          <div
            v-if="currentSectionImages.length > 0"
            class="images"
          >
            <files
              :files="currentSectionImages"
              actions="loadFiles,preview"
              :show-video="true"
              :full-width="true"
            />
          </div>
        </template>
      </div>
    </div>
  </Sidesheet>
</template>

<script>
import api from "@/plugins/api";
import formatAsCurrency from "@/utils/formatAsCurrency";
import { getFileName, getFileType } from "@/utils/files";
import { copyToClipboard } from "@/utils/browser/copy-to-clipboard";

import Sidesheet from "@/components/scrollable-sidesheet";
import Loader from "@/components/loader";
import Switcher from "@/components/controls2/Switcher.vue";
import Files from "@/components/files";
import SvgIcon from "@/components/svg-icon";

export default {
  components: {
    Sidesheet,
    Loader,
    Switcher,
    Files,
    SvgIcon,
  },
  data: () => ({
    active: false,
    busy: false,
    item: null,
    type: null,
    category: null,
    subcategory: null,
    currentSection: null,
  }),
  computed: {
    hasItem () {
      return !!this.item;
    },
    hasType () {
      return !!this.type;
    },
    sections () {
      if (!this.active) {
        return [];
      }

      return [
        {
          id: "item",
          name: "ТМЦ",
          data: this.item,
        },
        {
          id: "type",
          name: "Тип ТМЦ",
          data: this.type,
        },
        {
          id: "subcategory",
          name: "Подкатегория ТМЦ",
          data: this.subcategory,
        },
        {
          id: "category",
          name: "Категория ТМЦ",
          data: this.category,
        },
      ]
        .filter(({ data }) => !!data);
    },
    filteredSections () {
      const requiredFieldsMap = {
        item: {
          description: "description",
          inventoryFiles: "files",
        },
        type: {
          comment: "description",
          link: "link",
          typeUniformFiles: "files",
        },
        category: {
          description: "description",
          link: "link",
          typeUniformCategoryFiles: "files",
        },
        subcategory: {
          description: "description",
          link: "link",
          typeUniformCategoryFiles: "files",
        },
      };

      return this.sections.filter((section) => {
        const { data } = section;

        let isValid = false;

        Object.keys(requiredFieldsMap[section.id]).forEach((key) => {
          const target = data[key];
          const condition = Array.isArray(target) ? (target.length > 0) : (String(target || "").trim().length > 0);

          if (condition) {
            isValid = true;
          }
        });

        return isValid;
      })
        .map((section) => {
          const { data } = section;

          const formattedSection = {
            id: section.id,
            name: section.name,
            data: {
              name: data.form_view || data.name || "",
            },
          };

          Object.entries(requiredFieldsMap[section.id]).forEach(([from, to]) => {
            formattedSection.data[to] = data[from] || "";
          });

          return formattedSection;
        });
    },
    tabs () {
      return this.filteredSections.map((section) => ({
        value: section.id,
        label: section.name,
      }));
    },
    path () {
      const sections = this.sections.filter(part => part.id !== "item");

      sections.reverse();

      return sections.map(({ data }) => data.form_view || data.name).join(" / ");
    },
    currentSectionData () {
      const section = this.filteredSections.find(section => section.id === this.currentSection);

      if (!section) {
        return null;
      }

      return section.data;
    },
    currentSectionFiles () {
      if (!this.currentSectionData) {
        return [];
      }

      return (this.currentSectionData.files || []).map((file) => {
        const { name: url, id } = file;

        return {
          id,
          url,
          name: getFileName(url),
          type: getFileType(url),
        };
      });
    },
    currentSectionDocuments() {
      return this.currentSectionFiles.filter((file) => !["image", "video"].includes(file.type));
    },
    currentSectionImages() {
      return this.currentSectionFiles.filter((file) => ["image", "video"].includes(file.type));
    },
  },
  watch: {
    copied(v) {
      if (v) {
        setTimeout(() => {
          this.copied = false;
        }, 3 * 1000);
      }
    },
  },
  methods: {
    async fetchItem (id) {
      const { data } = await api.get("inventory/view", {
        params: {
          id,
        },
      });

      return data;
    },
    async fetchType (id) {
      const { data } = await api.get("inventory-type/view", {
        params: {
          id,
        },
      });

      return data;
    },
    async fetchCategory (id) {
      const { data } = await api.get("inventory-category/view", {
        params: {
          id,
        },
      });

      return data;
    },
    async open(itemId = null, typeId = null) {
      this.item = null;
      this.type = null;
      this.currentSection = null;

      if (!itemId && !typeId) {
        return;
      }

      this.active = true;

      this.busy = true;

      try {
        if (itemId) {
          this.item = await this.fetchItem(itemId);
          this.type = await this.fetchType(this.item.type.id);
          this.category = await this.fetchCategory(this.item.category.id);
          
          if (this.item.subcategory) {
            this.subcategory = await this.fetchCategory(this.item.subcategory.id);
          }
        } else if (typeId) {
          this.type = await this.fetchType(typeId);
          this.subcategory = await this.fetchCategory(this.type.parent_id);

          if (this.subcategory.parent_id) {
            this.category = await this.fetchCategory(this.subcategory.parent_id);
          }
        }

        if (this.filteredSections.length > 0) {
          this.currentSection = this.filteredSections[0].id;
        }
      } catch (error) {
        console.error({error});
        this.close();
      }

      this.busy = false;
    },
    close() {
      this.active = false;
    },
    formatAsCurrency (value) {
      return value ? formatAsCurrency(value) : null;
    },
    async copyLinkToClipboard() {
      if (!this.currentSectionData) {
        return;
      }

      await copyToClipboard(this.currentSectionData.link);

      this.$notify({
        group: "success-message",
        data: {
          textMessage: "Ссылка успешно скопирована в буфер обмена!",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
@import "~@/assets/styles/grid.scss";

.wrapper {
  max-width: 766px;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.guid {
  font-weight: 400;
}

.name {
  margin-top: -15px;
  font-size: 15px;
  line-height: 18px;
}

.path {
  margin-top: -14px;
  color: $color-label;
  font-size: 11px;
  line-height: 13px;
}

.parameters {
  display: flex;
  flex-wrap: wrap;
  padding: 8px 20px;
  gap: 6px 20px;
  font-size: 13px;
  line-height: 16px;
  border-radius: 15px;
  background-color: $color-bg;

  &__item {
    color: $color-label;
    flex-shrink: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    b {
      color: #000;
      font-weight: 500;
    }
  }
}

.description {
  font-size: 15px;
  line-height: 150%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.link {
  display: flex;
  align-items: center;
  font-size: 15px;
  
  &__copy {
    margin-left: 12px;
    background: none;
    border: none;
    outline: none;
    padding: 0;
    cursor: pointer;
  }
}
</style>
